import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn, LogOut, Play, Square, MapPin, Loader2, AlertTriangle, Navigation, PackageCheck, Boxes, PackageOpen, ParkingSquare, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { CapturePhoto } from '@/components/transport/CapturePhoto';
import { ScheduleInfo } from '@/components/transport/ScheduleInfo';
import { RejectStopDialog } from '@/components/transport/RejectStopDialog';
import { EmptyRunDialog } from '@/components/transport/EmptyRunDialog';
import { StopInfo, STOP_BADGE } from '@/components/transport/RunningTrips';
import { LocationHelp, MissedCheckoutBanner } from '@/components/transport/AttendanceBanners';
import { SelfiePunchDialog } from '@/components/transport/SelfiePunchDialog';
import { OdoChainCheck, chainState, emptyGap, gapError, gapPayload } from '@/components/transport/OdoChainCheck';
import { transportApi, getGPS } from '@/services/transportApi';

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const tripRoute = (t) => t.route_summary || `${t.pickup || '—'} → ${t.drop || '—'}`;
const isEmpty = (t) => t.trip_type === 'EMPTY_RUN';
const openStops = (t) => (isEmpty(t) ? [] : (t.stops || []).filter((s) => !['DONE', 'REJECTED'].includes(s.status || 'PENDING')));
const hasAwaiting = (t) => (t.stops || []).some((s) => s.acceptance === 'AWAITING');
const errMsg = (e, fb = 'Failed') => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || fb; };
const endedRecently = (t) => !t.end_time || (Date.now() - new Date(t.end_time).getTime()) < 24 * 3600 * 1000;

const MyTasks = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flow, setFlow] = useState(null); // {trip, mode:'start'|'end'}
  const [photo, setPhoto] = useState(null);
  const [photoPath, setPhotoPath] = useState(null);
  const [reading, setReading] = useState('');
  const [reading_busy, setReadingBusy] = useState(false);
  const [suspicious, setSuspicious] = useState(false);
  const [sharing, setSharing] = useState('idle'); // idle | on | denied
  const [rejectFor, setRejectFor] = useState(null); // {trip, stop}
  const [emptyFor, setEmptyFor] = useState(null); // {trip, direction}
  const [gpsHelp, setGpsHelp] = useState(null); // 'in' | 'out' when a punch had no location
  const [selfieFor, setSelfieFor] = useState(null); // 'in' | 'out' → selfie dialog open
  const [pending, setPending] = useState(null); // {kind, selfie} kept for a location retry
  const [link, setLink] = useState(null); // vehicle's last end reading (start flow)
  const [gap, setGap] = useState(emptyGap(null)); // unlogged run between last end and this start

  const load = useCallback(async () => {
    try { setData(await transportApi.myTasks()); } catch { toast.error('Failed to load tasks'); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Near-live tracking: while a trip is IN-TRANSIT, the phone pings its
  // location every 20s so the dispatch map can follow it.
  const inTransitIds = [...(data?.in_transit || []), ...(data?.returning || [])].map(t => t.id).join(',');
  useEffect(() => {
    const ids = inTransitIds ? inTransitIds.split(',') : [];
    if (ids.length === 0) { setSharing('idle'); return; }
    if (!navigator.geolocation) { setSharing('denied'); return; }
    let cancelled = false;
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          setSharing('on');
          const body = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
          for (const id of ids) { try { await transportApi.pingTrip(id, body); } catch { /* ignore */ } }
        },
        () => { if (!cancelled) setSharing('denied'); },
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 },
      );
    };
    ping();
    const iv = setInterval(ping, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [inTransitIds]);

  const punch = async (kind, shot) => {
    const selfie = shot || (pending?.kind === kind ? pending.selfie : null);
    if (!selfie) { setSelfieFor(kind); return; }
    setBusy(true);
    try {
      const gps = await getGPS(15000);
      if (!gps) { setPending({ kind, selfie }); setSelfieFor(null); setGpsHelp(kind); toast.error('Location is required — turn it on and try again'); return; }
      if (kind === 'in') { await transportApi.checkin({ gps, selfie }); toast.success('Checked in'); }
      else { await transportApi.checkout({ gps, selfie }); toast.success('Checked out'); }
      setGpsHelp(null); setPending(null); setSelfieFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Punch failed'); } finally { setBusy(false); }
  };

  const openRecheck = () => punch('in');

  const applyLink = (l) => { setLink(l); setGap(emptyGap(l)); };
  const openFlow = (trip, mode) => {
    setFlow({ trip, mode }); setPhoto(null); setPhotoPath(null); setReading(''); setSuspicious(false); applyLink(null);
    if (mode === 'start') transportApi.odoLink(trip.id).then(applyLink).catch(() => {});
  };

  const onPhoto = async (dataUrl) => {
    setPhoto(dataUrl); setReadingBusy(true);
    try {
      const res = await transportApi.readOdometer(dataUrl, flow.trip.vehicle_id, flow.mode);
      setPhotoPath(res.photo_path);
      if (res.reading != null) setReading(String(res.reading));
      setSuspicious(!!res.suspicious);
      if (res.reading != null) toast.success(`AI read: ${res.reading} km (edit if wrong)`);
      else toast.message('Could not auto-read — enter the odometer manually');
    } catch { toast.error('Odometer read failed — enter manually'); } finally { setReadingBusy(false); }
  };

  const submitFlow = async () => {
    const needsReading = flow.mode === 'start' || flow.mode === 'end';
    const needsPhoto = ['start', 'end', 'deliver'].includes(flow.mode);
    if (needsPhoto && !photo) { toast.error('Take the odometer photo first — it is required'); return; }
    if (needsReading && (!reading || isNaN(Number(reading)))) { toast.error('Enter the odometer reading'); return; }
    const chain = flow.mode === 'start' ? chainState(link, reading) : {};
    if (chain.below) { toast.error(`Lower than the last end reading (${link.last_end} km) — check the photo and correct it`); return; }
    if (chain.needsGap && gapError(gap)) { toast.error(gapError(gap)); return; }
    const gap_run = chain.needsGap ? gapPayload(gap) : undefined;
    const odoPhoto = photoPath || photo; // saved even if the auto-read failed
    setBusy(true);
    try {
      const gps = await getGPS();
      if (flow.mode === 'ready') {
        await transportApi.readyToLoad(flow.trip.id, { gps });
        toast.success('Marked ready to load — loading timer started');
      } else if (flow.mode === 'start') {
        await transportApi.startTrip(flow.trip.id, { start_odometer: Number(reading), start_odo_photo: odoPhoto, gps, gap_run });
        toast.success(gap_run ? `Trip started — unlogged run of ${chain.diff} km saved` : 'Trip started — departed');
      } else if (flow.mode === 'deliver') {
        const res = await transportApi.deliverTrip(flow.trip.id, { delivery_odometer: reading ? Number(reading) : undefined, delivery_odo_photo: odoPhoto, gps });
        toast.success(res.backhaul_assigned ? '📦 Delivered — a backhaul pickup was added on your way back!' : '📦 Delivered — now returning to base');
      } else if (flow.mode === 'unloading') {
        const res = await transportApi.unloadingComplete(flow.trip.id, { gps });
        toast.success(res.auto_assigned ? `Unloading complete — vehicle freed, ${res.auto_assigned} pending request(s) auto-assigned` : 'Unloading complete — vehicle freed');
      } else {
        const res = await transportApi.endTrip(flow.trip.id, { end_odometer: Number(reading), end_odo_photo: odoPhoto, gps, close_open_stops: openStops(flow.trip).length > 0 });
        toast.success(res.status === 'UNLOADING' ? `Arrived — ${res.distance} km · tap Unloading Complete when unloading is done` : `Trip completed — ${res.distance} km`);
      }
      setFlow(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.link) applyLink(d.link);
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const stopAction = async (trip, stop, kind) => {
    setBusy(true);
    try {
      const gps = await getGPS();
      if (kind === 'arrive') await transportApi.arriveStop(trip.id, stop.stop_id, { gps });
      else if (kind === 'accept') { await transportApi.acceptLiveStop(trip.id, stop.stop_id); toast.success(`Accepted — ${stop.label} added to your route`); }
      else await transportApi.doneStop(trip.id, stop.stop_id, { gps });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Stop update failed'); } finally { setBusy(false); }
  };
  const allStopsDone = (t) => !hasAwaiting(t);
  const emptyBtn = (direction) => (t) => (isEmpty(t) || (t.empty_run_dirs || []).includes(direction) || (direction === 'TO_PARKING' && !endedRecently(t))) ? null : (
    <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700" onClick={() => setEmptyFor({ trip: t, direction })} data-testid={`mytasks-emptyrun-${direction === 'TO_PICKUP' ? 'topickup' : 'toparking'}-${t.id}`}>
      <ParkingSquare className="w-4 h-4 mr-1" />{direction === 'TO_PICKUP' ? 'Empty run: Parking → Pickup' : 'Empty run: Drop → Parking'}
    </Button>
  );
  const emptyStart = (t) => (isEmpty(t) ? { fn: () => openFlow(t, 'start'), label: 'Start Empty Run', icon: Play, color: 'indigo' } : null);
  const emptyEnd = (t) => (isEmpty(t) ? { fn: () => openFlow(t, 'end'), label: 'End Empty Run — Reached', icon: Square, color: 'indigo', noAction2: true } : null);

  if (data && data.linked === false) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <Header navigate={navigate} />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <Card className="border-0 shadow-md max-w-sm"><CardContent className="p-6" data-testid="mytasks-not-linked">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="font-semibold">No driver record linked</p>
            <p className="text-sm text-gray-500 mt-1">Ask the Transport Manager to link your login to a driver in Driver Master.</p>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-my-tasks-page">
      <Header navigate={navigate} name={data?.driver_name} />
      <main className="max-w-xl mx-auto px-4 py-5 space-y-5">
        {/* Attendance */}
        <Card className="border-0 shadow-md"><CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold">Attendance</p>
              <p className="text-sm text-gray-500" data-testid="mytasks-attendance-status">
                {data?.checked_in ? 'On duty (checked in)' : data?.has_sessions ? 'Checked out' : 'Not checked in'}
                {data?.session_count > 1 && <span className="text-gray-400"> · {data.session_count} sessions today</span>}
              </p>
            </div>
            {data?.checked_in ? (
              <Button onClick={() => punch('out')} disabled={busy} variant="outline" data-testid="mytasks-checkout-btn"><LogOut className="w-4 h-4 mr-1" />Check Out</Button>
            ) : data?.has_sessions ? (
              <Button onClick={openRecheck} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="mytasks-recheckin-btn"><LogIn className="w-4 h-4 mr-1" />Re-Check In</Button>
            ) : (
              <Button onClick={() => punch('in')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mytasks-checkin-btn"><LogIn className="w-4 h-4 mr-1" />Check In</Button>
            )}
          </div>
          {gpsHelp && <div className="mt-3"><LocationHelp onRetry={() => punch(gpsHelp)} onClose={() => { setGpsHelp(null); setPending(null); }} busy={busy} /></div>}
        </CardContent></Card>

        {!data?.has_sessions && <MissedCheckoutBanner prev={data?.prev_missed_checkout} />}

        {data?.in_transit?.length > 0 && (
          <div data-testid="mytasks-sharing-chip">
            {sharing === 'on' ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="mytasks-sharing-on">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Sharing location with dispatch
              </span>
            ) : sharing === 'denied' ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-full px-3 py-1.5" data-testid="mytasks-sharing-denied">
                <AlertTriangle className="w-3 h-3" />Location is off — turn it on so dispatch can see you
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-400" data-testid="mytasks-sharing-start"><Loader2 className="w-3 h-3 animate-spin" />Starting location sharing…</span>
            )}
          </div>
        )}

        <TripGroup title="Unloading — at drop" trips={data?.unloading} color="fuchsia" action={(t) => openFlow(t, 'unloading')} actionLabel="Unloading Complete" actionIcon={PackageCheck} testPrefix="unloading" extra={emptyBtn('TO_PARKING')} />
        <TripGroup title="In-Transit" trips={data?.in_transit} color="teal" action={(t) => openFlow(t, 'end')} actionLabel="End Trip — Arrived" actionIcon={Square} testPrefix="intransit" action2={(t) => openFlow(t, 'deliver')} action2Label="Delivered (one-way)" action2Icon={PackageOpen} showStops onStopArrive={(t, s) => stopAction(t, s, 'arrive')} onStopDone={(t, s) => stopAction(t, s, 'done')} onStopAccept={(t, s) => stopAction(t, s, 'accept')} onStopReject={(t, s) => setRejectFor({ trip: t, stop: s })} actionDisabledFn={(t) => !isEmpty(t) && !allStopsDone(t)} resolveAction={emptyEnd} busy={busy} />
        <TripGroup title="Returning to base" trips={data?.returning} color="indigo" action={(t) => openFlow(t, 'end')} actionLabel="End — Back at Base" actionIcon={Square} testPrefix="returning" />
        <TripGroup title="At Pickup — Loading" trips={data?.ready_to_load} color="emerald" action={(t) => openFlow(t, 'start')} actionLabel="Start Trip — Depart" actionIcon={Play} testPrefix="loading" extra={emptyBtn('TO_PICKUP')} />
        <TripGroup title="Assigned" trips={data?.assigned} color="amber" action={(t) => openFlow(t, 'ready')} actionLabel="Vehicle Ready to Load" actionIcon={Boxes} testPrefix="assigned" resolveAction={emptyStart} extra={emptyBtn('TO_PICKUP')} />
        <TripGroup title="Completed (recent)" trips={data?.completed} color="green" testPrefix="completed" extra={emptyBtn('TO_PARKING')} />
      </main>

      <RejectStopDialog target={rejectFor} onClose={() => setRejectFor(null)} onDone={() => { setRejectFor(null); load(); }} />
      <SelfiePunchDialog kind={selfieFor} busy={busy} onConfirm={(shot) => punch(selfieFor, shot)} onClose={() => setSelfieFor(null)} />
      <EmptyRunDialog target={emptyFor} onClose={() => setEmptyFor(null)} onDone={() => { setEmptyFor(null); load(); }} />

      <Dialog open={!!flow} onOpenChange={(o) => !o && setFlow(null)}>
        <DialogContent data-testid="trip-flow-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{
            flow?.mode === 'ready' ? 'Vehicle Ready to Load'
              : flow?.mode === 'start' ? (flow?.trip && isEmpty(flow.trip) ? 'Start Empty Run — photograph odometer' : 'Start Trip — photograph odometer')
              : flow?.mode === 'deliver' ? 'Delivered — photograph odometer'
              : flow?.mode === 'unloading' ? 'Unloading Complete'
              : (flow?.trip && isEmpty(flow.trip) ? 'End Empty Run — photograph odometer' : 'End Trip — photograph odometer')
          }</DialogTitle><DialogDescription className="sr-only">Trip action step</DialogDescription></DialogHeader>
          {flow && <p className="text-sm text-gray-500">{tripRoute(flow.trip)} · {flow.trip.vehicle_label}</p>}
          {flow?.mode === 'ready' && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="ready-hint">Confirm the vehicle has reached the pickup and loading is starting. We'll time the gap until you tap <b>Start Trip</b> as the loading time.</p>}
          {flow?.mode === 'deliver' && <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1.5" data-testid="deliver-hint">Mark the goods delivered. The vehicle switches to <b>Returning</b> — end the trip only when you're back at base. The odometer photo is required; the km reading here is optional but records an accurate one-way distance.</p>}
          {(flow?.mode === 'end' || flow?.mode === 'deliver') && flow.trip.status === 'IN_TRANSIT' && openStops(flow.trip).length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid="open-stops-note">
              {openStops(flow.trip).length} stop(s) not marked Done yet ({openStops(flow.trip).map((s) => s.label).join(', ')}) — they will be marked Done now.
            </p>
          )}
          {flow?.mode === 'end' && (isEmpty(flow.trip)
            ? <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5" data-testid="end-hint">You've reached the end of the empty run. The km is saved as an <b>Empty run</b>.</p>
            : <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5" data-testid="end-hint">You've reached the destination. You're freed for the next task now; the vehicle stays here until someone marks <b>Unloading Complete</b>.</p>)}
          {flow?.mode === 'unloading' && <p className="text-xs text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded px-2 py-1.5" data-testid="unloading-hint">Confirm unloading is finished. This frees the vehicle so a waiting request can be auto-assigned.</p>}
          {(flow?.mode === 'start' || flow?.mode === 'deliver' || flow?.mode === 'end') && (
            <div className="space-y-3">
              <CapturePhoto label="Photograph odometer (required)" onCapture={onPhoto} busy={reading_busy} captured={!!photo} testId="odo-capture" />
              <div>
                <Label>Odometer reading (km){flow?.mode === 'deliver' ? ' — optional' : ' — editable'}</Label>
                <Input type="number" value={reading} onChange={e => setReading(e.target.value)} placeholder="e.g. 45120" data-testid="odo-reading-input" />
                {reading_busy && <p className="text-xs text-amber-600 flex items-center mt-1"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Reading photo…</p>}
                {suspicious && !(flow?.mode === 'start' && link?.last_end != null) && <p className="text-xs text-red-600 mt-1" data-testid="odo-suspicious">⚠ Lower than the last recorded reading — please double-check.</p>}
              </div>
              {flow?.mode === 'start' && <OdoChainCheck link={link} reading={reading} gap={gap} setGap={setGap} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlow(null)}>Cancel</Button>
            <Button onClick={submitFlow} disabled={busy || (['start', 'end', 'deliver'].includes(flow?.mode) && !photo)} className={
              flow?.mode === 'ready' ? 'bg-amber-500 hover:bg-amber-600'
                : flow?.mode === 'start' ? 'bg-emerald-600 hover:bg-emerald-700'
                : flow?.mode === 'deliver' ? 'bg-teal-600 hover:bg-teal-700'
                : flow?.mode === 'unloading' ? 'bg-fuchsia-600 hover:bg-fuchsia-700'
                : 'bg-indigo-600 hover:bg-indigo-700'} data-testid="trip-flow-submit">
              {flow?.mode === 'ready' ? 'Confirm Ready to Load'
                : flow?.mode === 'unloading' ? 'Unloading Complete'
                : flow?.trip && isEmpty(flow.trip) ? (flow?.mode === 'start' ? 'Start Empty Run' : 'End Empty Run')
                : flow?.mode === 'start' ? 'Start Trip'
                : flow?.mode === 'deliver' ? 'Delivered — Return'
                : 'Arrived — End Trip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Header = ({ navigate, name }) => (
  <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
    <div className="max-w-xl mx-auto px-4 h-16 flex items-center gap-4">
      <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
      <div><h1 className="text-xl font-bold font-display">My Tasks</h1>{name && <p className="text-sm text-[#E8DCC8]">{name}</p>}</div>
    </div>
  </header>
);

const BTN_COLORS = {
  amber: 'bg-amber-500 hover:bg-amber-600', teal: 'bg-teal-600 hover:bg-teal-700',
  indigo: 'bg-indigo-600 hover:bg-indigo-700', emerald: 'bg-emerald-600 hover:bg-emerald-700',
  fuchsia: 'bg-fuchsia-600 hover:bg-fuchsia-700', green: 'bg-green-600 hover:bg-green-700',
};

const TripGroup = ({ title, trips = [], testPrefix, ...rest }) => (
  <div data-testid={`mytasks-group-${testPrefix}`}>
    <h3 className="text-sm font-semibold text-gray-600 mb-2">{title} ({trips?.length || 0})</h3>
    <div className="space-y-2">
      {(!trips || trips.length === 0) && <p className="text-xs text-gray-400">None</p>}
      {trips?.map((t) => <TripCard key={t.id} t={t} {...rest} />)}
    </div>
  </div>
);

const StopRow = ({ t, s, si, busy, onStopArrive, onStopDone, onStopAccept, onStopReject }) => {
  const st = s.status || 'PENDING';
  const awaiting = s.acceptance === 'AWAITING';
  const open = !['DONE', 'REJECTED'].includes(st);
  const btn = 'h-6 px-2 text-[11px]';
  return (
    <div className={`flex flex-wrap items-start justify-between gap-2 rounded px-2 py-1.5 ${awaiting ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`} data-testid={`mytasks-stop-${t.id}-${si}`}>
      <div className="flex-1 basis-40 min-w-0"><StopInfo s={s} /></div>
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
        <Badge className={STOP_BADGE[st]} data-testid={`mytasks-stop-status-${t.id}-${si}`}>{st}</Badge>
        {awaiting ? (
          <>
            <Button size="sm" className={`${btn} bg-emerald-600 hover:bg-emerald-700`} disabled={busy} onClick={() => onStopAccept(t, s)} data-testid={`mytasks-stop-accept-${t.id}-${si}`}><Check className="w-3 h-3 mr-0.5" />Accept</Button>
            <Button size="sm" variant="outline" className={`${btn} border-red-300 text-red-600`} disabled={busy} onClick={() => onStopReject(t, s)} data-testid={`mytasks-stop-reject-${t.id}-${si}`}><X className="w-3 h-3 mr-0.5" />Reject</Button>
          </>
        ) : (
          <>
            {st === 'PENDING' && <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => onStopArrive(t, s)} data-testid={`mytasks-stop-arrive-${t.id}-${si}`}>Arrived</Button>}
            {open && <Button size="sm" className={`${btn} bg-green-600 hover:bg-green-700`} disabled={busy} onClick={() => onStopDone(t, s)} data-testid={`mytasks-stop-done-${t.id}-${si}`}>Done</Button>}
            {open && s.added_live && <Button size="sm" variant="ghost" className={`${btn} text-red-600`} disabled={busy} onClick={() => onStopReject(t, s)} data-testid={`mytasks-stop-reject-${t.id}-${si}`}>Reject</Button>}
          </>
        )}
      </div>
    </div>
  );
};

const TripCard = ({ t, color, action, actionLabel, actionIcon, action2, action2Label, action2Icon: AI2, showStops, actionDisabledFn, resolveAction, extra, busy, ...stopFns }) => {
  const stops = t.stops || [];
  const closed = stops.filter((s) => ['DONE', 'REJECTED'].includes(s.status || 'PENDING')).length;
  const awaiting = stops.filter((s) => s.acceptance === 'AWAITING').length;
  const disabled = actionDisabledFn ? actionDisabledFn(t) : false;
  const over = resolveAction?.(t);
  const act = over || (action ? { fn: () => action(t), label: actionLabel, icon: actionIcon, color } : null);
  const AI = act?.icon;
  const extraEl = extra?.(t);
  return (
          <Card className={`border-0 shadow-sm ${awaiting ? 'ring-2 ring-amber-300' : ''}`} data-testid={`mytasks-trip-${t.id}`}>
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  {isEmpty(t) && <Badge className="bg-indigo-100 text-indigo-700 mb-1" data-testid={`mytasks-emptyrun-badge-${t.id}`}><ParkingSquare className="w-3 h-3 mr-1" />Empty run</Badge>}
                  {awaiting > 0 && <p className="text-[11px] font-semibold text-amber-700 mb-1" data-testid={`mytasks-awaiting-${t.id}`}>New stop added — Accept or Reject below</p>}
                  <p className="font-semibold text-sm flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" />{tripRoute(t)}</p>
                  <ScheduleInfo r={t} testId={`mytasks-needed-${t.id}`} />
                  <p className="text-xs text-gray-500">{t.vehicle_label} · {t.section || t.purpose || ''}{t.distance != null ? ` · ${t.distance} km` : (t.estimated_distance_km != null ? ` · ~${t.estimated_distance_km} km` : '')}</p>
                  {t.vehicle_location && (
                    <p className="text-[11px] text-gray-600 mt-1 flex items-center gap-1 flex-wrap" data-testid={`mytasks-vehicle-loc-${t.id}`}>
                      <Navigation className="w-3 h-3 text-teal-600" />
                      Vehicle now at: <span className="font-medium">{t.vehicle_location.label || 'Last drop'}</span>
                      {t.vehicle_location.source === 'live' && <span className="inline-flex items-center gap-1 text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />live</span>}
                      {t.vehicle_location.source === 'home_base' && <span className="text-gray-400">(home base)</span>}
                      {t.vehicle_location.nav_url && <a href={t.vehicle_location.nav_url} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`mytasks-vehicle-nav-${t.id}`}>Navigate</a>}
                    </p>
                  )}
                  {t.eta && (
                    <p className="text-[11px] text-teal-700 font-medium mt-0.5 flex items-center gap-1" data-testid={`mytasks-eta-${t.id}`}>
                      <Navigation className="w-3 h-3" />ETA ~{fmtTime(t.eta.eta_at)} · ~{t.eta.remaining_km} km left
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {act && <Button size="sm" onClick={act.fn} disabled={!over && disabled} title={!over && disabled ? 'Accept or Reject the new stop first' : ''} className={BTN_COLORS[act.color] || BTN_COLORS.indigo} data-testid={`mytasks-action-${t.id}`}>{AI && <AI className="w-4 h-4 mr-1" />}{act.label}</Button>}
                  {action2 && !over?.noAction2 && <Button size="sm" variant="outline" onClick={() => action2(t)} disabled={disabled} data-testid={`mytasks-action2-${t.id}`}>{AI2 && <AI2 className="w-4 h-4 mr-1" />}{action2Label}</Button>}
                </div>
              </div>
              {extraEl && <div className="mt-2">{extraEl}</div>}
              {showStops && !isEmpty(t) && stops.length > 0 && (
                <div className="mt-3 border-t pt-2 space-y-1.5" data-testid={`mytasks-stops-${t.id}`}>
                  <p className="text-[11px] text-gray-500">Stops closed {closed}/{stops.length} — mark each as you go; any still open are marked Done when you end the trip</p>
                  {stops.map((s, si) => <StopRow key={s.stop_id || si} t={t} s={s} si={si} busy={busy} {...stopFns} />)}
                </div>
              )}
            </CardContent>
          </Card>
  );
};

export default MyTasks;
